import { FC, useMemo } from 'react'

import { DeepPartial } from '@apollo/client/utilities'
import { F0Alert, F0Box } from '@factorialco/f0-react'
import { EmployeeTableViewRowFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { TaskAffecteeInfoFragment } from 'generated/resources/graphql'
import {
  EmployeeTableViewRow,
  useEmployeeTableViewRow,
} from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'

import { ApprovableContentComponent } from 'modules/inbox_tasks/tasks/approval/types'

import { RESOURCE_COMPONENTS } from './helpers'

// Widen the type so a plain `string` key can be used for lookup
const resourceComponentsByName: Record<string, ApprovableContentComponent | undefined> =
  RESOURCE_COMPONENTS

type Approvable = {
  __typename?: string
  id?: string
}

type ApprovalContentProps = {
  approvable?: Approvable | null
  owner?: {
    id?: string
    employeeName?: EmployeeTableViewRowFragment | DeepPartial<EmployeeTableViewRowFragment> | null
  } | null
  reason?: string | null
  resourceId?: string | null
  resourceType?: string | null
  status?: string | null
  affectee?: TaskAffecteeInfoFragment
  /** Pass to override the internally computed requester content when the caller already has it */
  requesterContent?: EmployeeTableViewRow
}

const ApprovalContent: FC<ApprovalContentProps> = ({
  approvable,
  owner,
  reason,
  resourceId: resourceIdProp,
  resourceType,
  status,
  affectee,
  requesterContent: requesterContentProp,
}) => {
  const computedResourceId = useMemo(() => {
    return approvable?.id ?? resourceIdProp ?? undefined
  }, [approvable, resourceIdProp])

  const resourceTypename = approvable?.__typename ?? ''
  const isRejected = status === 'rejected'

  const internalRequesterContent = useEmployeeTableViewRow({
    employee: owner?.employeeName,
    title: 'Requester',
  })
  const requesterContent = requesterContentProp ?? internalRequesterContent

  const ownerId = owner?.employeeName?.id ?? undefined

  const Content = useMemo(() => {
    if (!computedResourceId || !resourceTypename) {
      return (
        <F0Alert
          title={i18n.t('common.approvals.no_permission.title')}
          description={i18n.t('common.approvals.no_permission.description')}
          variant="info"
        />
      )
    }

    const Component = resourceComponentsByName[resourceTypename]
    if (!Component) return null

    return (
      <Component
        id={computedResourceId}
        requesterContent={requesterContent}
        affectee={affectee}
        requesterEmployeeId={ownerId}
      />
    )
  }, [affectee, resourceTypename, computedResourceId, requesterContent, ownerId])

  return (
    <F0Box display="flex" flexDirection="column" gap="2xl" overflowX="hidden" grow>
      {Content}
      {computedResourceId && resourceType && (
        <ApprovalsInformationBlock
          resourceId={computedResourceId}
          resourceType={resourceType}
          newLook
        />
      )}
      {isRejected && !!reason && (
        <NoteField label={i18n.t('settings.approvals.rejection_reason')} content={reason} />
      )}
    </F0Box>
  )
}

export default ApprovalContent
